import { updateProfile, type AuthProvider, type User } from 'firebase/auth';
import { getCurrentUser } from '@guuru/firebase-web';
import { client } from '@guuru/graphql-web';
import firebase from './auth';
import { AUTH_ERROR_MESSAGES } from './authError';
import UPDATE_PUSH_TOKEN_WEB from './mutations/UpdatePushTokenWeb';

const { auth } = firebase;

let signInErrorMessage: string | undefined;

const getSignInErrorMessage = () => signInErrorMessage;

const setSignInErrorMessage = (message?: string) => {
  signInErrorMessage = message;
};

const getCurrentUserId = async () => {
  const user = await getCurrentUser();
  return user?.uid;
};

const getCurrentUserToken = async () => {
  const user = await getCurrentUser();
  return user?.getIdToken();
};

const signInWithPassword = (email: string, password: string) => {
  setSignInErrorMessage();
  return firebase.signInWithEmailAndPassword(auth, email, password);
};

const signUpWithPassword = (email: string, password: string) => {
  setSignInErrorMessage();
  return firebase.createUserWithEmailAndPassword(auth, email, password);
};

const deleteFirebaseUser = (user: User) => user.delete();

const updateUserName = async (user: User, name: string) => {
  await updateProfile(user, { displayName: name.trim() });
  await user.getIdToken(true);
};

const signInAnonymously = async () => {
  const { user } = await firebase.signInAnonymously(auth);
  return user.uid;
};

const signInWithCustomToken = (customToken: string) =>
  firebase.signInWithCustomToken(auth, customToken);

const signInWithProvider = async (provider: AuthProvider) => {
  setSignInErrorMessage();
  const credential = await firebase.signInWithPopup(auth, provider);
  const additionalUserInfo = firebase.getAdditionalUserInfo(credential);
  if (additionalUserInfo?.isNewUser) {
    setSignInErrorMessage(AUTH_ERROR_MESSAGES['auth/user-not-found']);
    await deleteFirebaseUser(credential.user);
  }
};

const signInWithGoogle = () =>
  signInWithProvider(new firebase.GoogleAuthProvider());

const signInWithMicrosoft = () =>
  signInWithProvider(new firebase.OAuthProvider('microsoft.com'));

const signUpWithProvider = async (provider: AuthProvider) => {
  setSignInErrorMessage();
  await firebase.signInWithPopup(auth, provider);
};

const signUpWithGoogle = () =>
  signUpWithProvider(new firebase.GoogleAuthProvider());

const signUpWithMicrosoft = () =>
  signUpWithProvider(new firebase.OAuthProvider('microsoft.com'));

const signUpAnonymously = async () => {
  client.close();
  await firebase.signOut(auth);
  return signInAnonymously();
};

const signOut = async ({ keepPushToken = false } = {}) => {
  const user = await getCurrentUser();
  if (user && !user.isAnonymous && !keepPushToken) {
    await client.mutate({
      mutation: UPDATE_PUSH_TOKEN_WEB,
      variables: {
        id: user.uid,
        pushTokenWeb: null,
      },
    });
  }
  return firebase.signOut(auth);
};

export {
  auth,
  getCurrentUser,
  getCurrentUserId,
  getCurrentUserToken,
  getSignInErrorMessage,
  deleteFirebaseUser,
  signInAnonymously,
  signInWithPassword,
  signUpWithPassword,
  updateUserName,
  signInWithCustomToken,
  signInWithGoogle,
  signInWithMicrosoft,
  signUpWithGoogle,
  signUpWithMicrosoft,
  signUpAnonymously,
  signOut,
};

export type { User };
